import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import CustomButton from "../../../components/CustomButton";
import Pagination from "../../../components/CustomPagination";
import SkeletonLoader from "../../../components/SkeletonLoader";
import {
  useGetAllSponsorUserList,
  useGetParentsponsorDetails,
  useMetrics,
} from "../../../custom-hooks/queries";
import { UserDashboardStats } from "../../../components/UserStats";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { useMemo, useCallback } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import MainSearchBar from "../../../components/MainSearchbar";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import { useScreenWidth } from "../../../custom-hooks/useScreenWidth";
import CustomTable from "../../../components/CustomTable";

// Status indicator component
const StatusIndicator = ({ isActive }) => {
  return isActive ? (
    <span className="inline-block px-2 py-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-300 rounded-md">
      Active
    </span>
  ) : (
    <span className="inline-block px-2 py-1 text-xs font-semibold text-yellow-700 bg-yellow-50 border border-yellow-300 rounded-md">
      Inactive
    </span>
  );
};

// Action dropdown component
const UserActionDropdown = ({ userId, onManageMember }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = React.useRef();

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button onClick={handleToggle} className="focus:outline-none">
        <span className="text-2xl">⋮</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg z-10">
          <div className="py-1">
            <button
              onClick={() => {
                onManageMember(userId);
                setOpen(false);
              }}
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
            >
              Manage User
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ManageTeam = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screenWidth = useScreenWidth();

  // Add search state
  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["teamMembers"]
    ) || "";

  // Add sort state
  const [sortConfig, setSortConfig] = useState({});

  const [teamMembers, setTeamMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");
  const name = searchParams.get("name");
  const { data } = useMetrics(id);

  const {
    data: parentUserData,
    isLoading: detailLoading,
    isError,
  } = useGetParentsponsorDetails(id);

  const encodedId = parentUserData?.hypercomply_org_id != null
    ? btoa(String(parentUserData.hypercomply_org_id))
    : "";

  // Update the query to include the search parameter
  const { data: sponsorsList, isLoading } = useGetAllSponsorUserList({
    currentPage,
    id,
    search_text: searchQuery, // Add search query parameter
  });

  const stats = [
    { name: "Total Users", value: sponsorsList?.total_items },
    { name: "Total Requests", value: data?.total_count },
    { name: "Active Requests", value: data?.pending_count },
  ];
  // Add debounced search handler
  const debouncedSearch = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({
          pageKey: "teamMembers",
          searchText: search,
        })
      );
    }, 300),
    [dispatch]
  );

  const onSearchSubmit = (search) => {
    debouncedSearch(search);
  };

  // Reset page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Add sort handler function
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      [key]: {
        direction: prev[key]?.direction === "asc" ? "desc" : "asc",
      },
    }));
  };
  // We don't need getSortIcon anymore as CustomTable handles this

  // Add sorted items using useMemo
  const sortedItems = useMemo(() => {
    if (!sponsorsList?.items?.length) return [];

    const sortKey = Object.keys(sortConfig)[0];
    if (!sortKey) return sponsorsList.items; // No sorting applied

    const { direction } = sortConfig[sortKey];

    return [...sponsorsList.items].sort((a, b) => {
      // Special cases for combined fields like name
      if (sortKey === "name") {
        const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
        return direction === "asc"
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      }

      // Handle null/undefined values - move them to the end
      if (a[sortKey] === null || a[sortKey] === undefined)
        return direction === "asc" ? 1 : -1;
      if (b[sortKey] === null || b[sortKey] === undefined)
        return direction === "asc" ? -1 : 1;

      // Special case for status (is_active) - booleans should sort true first
      if (
        sortKey === "is_active" &&
        typeof a[sortKey] === "boolean" &&
        typeof b[sortKey] === "boolean"
      ) {
        return direction === "asc"
          ? a[sortKey] === b[sortKey]
            ? 0
            : a[sortKey]
            ? -1
            : 1
          : a[sortKey] === b[sortKey]
          ? 0
          : a[sortKey]
          ? 1
          : -1;
      }

      // Handle string comparison (case insensitive)
      if (typeof a[sortKey] === "string" && typeof b[sortKey] === "string") {
        return direction === "asc"
          ? a[sortKey].toLowerCase().localeCompare(b[sortKey].toLowerCase())
          : b[sortKey].toLowerCase().localeCompare(a[sortKey].toLowerCase());
      }

      // Default comparison for other types
      return direction === "asc"
        ? a[sortKey] > b[sortKey]
          ? 1
          : -1
        : a[sortKey] < b[sortKey]
        ? 1
        : -1;
    });
  }, [sponsorsList, sortConfig]);

  // Existing useEffect and handlers...

  const handleAddNewMember = () => {
    const qs = new URLSearchParams({ id, name: name ?? "", orgId: encodedId }).toString();
    navigate(`/Sponsor/AddTeamMember?${qs}`);
  };

  const handleManageMember = (userId) => {
    const qs = new URLSearchParams({
      userId,
      id,
      name: name ?? "",
      orgId: encodedId,
    }).toString();
    navigate(`/Sponsor/AddTeamMember?${qs}`);
  };

  if (isLoading) {
    return (
      <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
        <SkeletonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
        <p className="text-red-600">
          Error fetching data. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen bg-gray-50">
      <div className="border-b border-gray-200 py-5 flex items-center justify-between">
        <h1 className="font-semibold flex items-center">
          <span>Team Members</span>
        </h1>
      </div>

      <section className="-mx-4 sm:-mx-6 lg:-mx-8 border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <UserDashboardStats stats={stats} />
        </div>
      </section>

      <div className="mt-8 mx-1 bg-white shadow-md rounded-md p-6 border border-gray-300">
        {/* Update this section to include search bar */}
        <div className="flex justify-between items-center mb-5">
          <div>
            <MainSearchBar
              pageKey="teamMembers"
              placeholder="Search team members..."
              height="40px"
              width={
                screenWidth <= 375
                  ? "17.5rem"
                  : screenWidth < 425
                  ? "22rem"
                  : "25rem"
              }
              marginRight={"mr-2"}
              isSearchIcon={true}
              onSearch={onSearchSubmit}
            />
          </div>
          <div>
            <CustomButton
              color="bg-indigo-600"
              size="text-base"
              weight="font-normal"
              height="h-9"
              width="w-48"
              textColor="text-white"
              text="Add Team Member"
              borderRadius="0.375rem"
              onClick={handleAddNewMember}
            />
          </div>
        </div>{" "}
        {/* CustomTable component to replace the existing table */}
        <CustomTable
          columns={[
            {
              key: "name",
              header: "User Name",
              sortable: true,
              cellClassName:
                "hover:text-gray-700 hover:cursor-pointer hover:underline",
              render: (member) => (
                <span>
                  {member.first_name} {member.last_name}
                </span>
              ),
            },
            {
              key: "email",
              header: "Email",
              sortable: true,
            },
            {
              key: "role",
              header: "Role",
              sortable: true,
              render: (member) =>
                member?.sponsor_subrole_dto
                  ?.map((role) => role.sub_role)
                  .join("/"),
            },
            {
              key: "is_active",
              header: "Status",
              sortable: true,
              render: (member) => (
                <StatusIndicator isActive={member.is_active} />
              ),
            },
            {
              key: "actions",
              header: "Actions",
              align: "right",
              sortable: false,
              render: (member) => (
                <UserActionDropdown
                  userId={member.id}
                  onManageMember={handleManageMember}
                />
              ),
            },
          ]}
          data={sortedItems || []}
          sortConfig={sortConfig}
          onSort={handleSort}
          emptyMessage="No team members found"
          emptyDescription={
            searchQuery
              ? "Your search did not match any team members. Please try again."
              : "Your organization doesn't have any team members yet."
          }
          isSearchActive={!!searchQuery}
          className="shadow-sm"
        />
        {sortedItems && sortedItems.length > 0 && (
          <div className="mt-5">
            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={sponsorsList?.total_pages || 1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageTeam;
